import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Building2, Users, Briefcase, DollarSign, Clock, CheckCircle, 
  RefreshCw, AlertTriangle, ChevronRight, Shield, Mail
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SuperAdminOverview = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [pendingItems, setPendingItems] = useState([]);
  const [recentActivity, setRecentActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // 2FA State
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [showDisable2FA, setShowDisable2FA] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  
  const token = localStorage.getItem('movemate_token');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, pendingRes, activityRes, mfaRes] = await Promise.all([
        fetch(`${API_URL}/api/superadmin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/superadmin/pending-items`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/superadmin/recent-activity`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/superadmin/2fa-status`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      if (statsRes.ok) setStats(await statsRes.json());
      if (pendingRes.ok) {
        const data = await pendingRes.json();
        setPendingItems(data.items || []);
      }
      if (activityRes.ok) setRecentActivity(await activityRes.json());
      if (mfaRes.ok) {
        const mfaData = await mfaRes.json();
        setMfaEnabled(mfaData.mfa_enabled || false);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 2FA Functions
  const handleEnable2FA = async () => {
    setMfaLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/superadmin/setup-2fa`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setShow2FASetup(true);
        toast.info('Verification code sent to your email');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to send verification code');
      }
    } catch (error) {
      toast.error('Failed to set up 2FA');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    setMfaLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/superadmin/enable-2fa`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ verification_code: verificationCode })
      });
      if (res.ok) {
        setMfaEnabled(true);
        setShow2FASetup(false);
        setVerificationCode('');
        toast.success('2FA enabled successfully!');
        
        // Update user in localStorage
        const user = JSON.parse(localStorage.getItem('movemate_user') || '{}');
        user.mfa_enabled = true;
        localStorage.setItem('movemate_user', JSON.stringify(user));
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Invalid verification code');
      }
    } catch (error) {
      toast.error('Failed to enable 2FA');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    setMfaLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/superadmin/disable-2fa`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password: disablePassword })
      });
      if (res.ok) {
        setMfaEnabled(false);
        setShowDisable2FA(false);
        setDisablePassword('');
        toast.success('2FA disabled');
        
        // Update user in localStorage
        const user = JSON.parse(localStorage.getItem('movemate_user') || '{}');
        user.mfa_enabled = false;
        localStorage.setItem('movemate_user', JSON.stringify(user));
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Invalid password');
      }
    } catch (error) {
      toast.error('Failed to disable 2FA');
    } finally {
      setMfaLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Platform Overview</h2>
        <Button variant="outline" size="sm" onClick={fetchData}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard icon={<Building2 />} value={stats?.total_companies} label="Companies" color="blue" />
        <StatCard icon={<Users />} value={stats?.total_users} label="Total Users" color="green" />
        <StatCard icon={<Briefcase />} value={stats?.total_jobs} label="Total Jobs" color="purple" />
        <StatCard icon={<Clock />} value={stats?.pending_item_approvals} label="Pending" color="amber" />
        <StatCard icon={<CheckCircle />} value={stats?.active_subscriptions} label="Active Subs" color="emerald" />
        <StatCard icon={<DollarSign />} value={`€${stats?.revenue_this_month?.toFixed(0) || 0}`} label="This Month" color="rose" />
      </div>

      {/* Pending Approvals Alert */}
      {stats?.pending_item_approvals > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-6 w-6 text-amber-600" />
                <div>
                  <p className="font-semibold text-amber-800">
                    {stats.pending_item_approvals} inventory item(s) awaiting approval
                  </p>
                  <p className="text-sm text-amber-600">
                    Review and approve custom inventory items submitted by companies
                  </p>
                </div>
              </div>
              <Button onClick={() => navigate('/superadmin/approvals')}>
                Review Now
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Signups */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Recent Company Signups</CardTitle>
            <CardDescription>Latest companies registered on the platform</CardDescription>
          </CardHeader>
          <CardContent>
            {recentActivity?.recent_signups?.length > 0 ? (
              <div className="space-y-3">
                {recentActivity.recent_signups.slice(0, 5).map((company, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">{company.name}</p>
                      <p className="text-sm text-gray-500">{company.email}</p>
                    </div>
                    <Badge variant="outline">
                      {new Date(company.created_at).toLocaleDateString()}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No recent signups</p>
            )}
            <Button variant="link" className="mt-4 w-full" onClick={() => navigate('/superadmin/companies')}>
              View All Companies
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Recent Activity</CardTitle>
            <CardDescription>Latest jobs and updates across the platform</CardDescription>
          </CardHeader>
          <CardContent>
            {recentActivity?.recent_jobs?.length > 0 ? (
              <div className="space-y-3">
                {recentActivity.recent_jobs.slice(0, 5).map((job, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">{job.reference}</p>
                      <p className="text-sm text-gray-500">{job.company_name}</p>
                    </div>
                    <Badge variant={job.status === 'completed' ? 'default' : 'secondary'}>
                      {job.status}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No recent jobs</p>
            )}
            <Button variant="link" className="mt-4 w-full" onClick={() => navigate('/superadmin/activity')}>
              View All Activity
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Pending Approvals Preview */}
      {pendingItems.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Pending Approvals</CardTitle>
            <CardDescription>Custom inventory items awaiting your review</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingItems.slice(0, 3).map((item, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <div>
                    <p className="font-medium">{item.name}</p>
                    <p className="text-sm text-gray-500">
                      {item.company_name} • {item.room || 'General'}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-amber-600 border-amber-400">
                    Pending
                  </Badge>
                </div>
              ))}
            </div>
            <Button className="mt-4 w-full" onClick={() => navigate('/superadmin/approvals')}>
              Review All ({pendingItems.length})
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Account Security - 2FA */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="h-5 w-5 text-amber-500" />
            Account Security
          </CardTitle>
          <CardDescription>Manage your two-factor authentication settings</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <h3 className="font-semibold">Two-Factor Authentication (2FA)</h3>
              <p className="text-sm text-gray-500">
                {mfaEnabled
                  ? 'Your account is protected with email-based 2FA'
                  : 'Enable 2FA for enhanced security'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={mfaEnabled}
                onCheckedChange={(checked) => {
                  if (checked) {
                    handleEnable2FA();
                  } else {
                    setShowDisable2FA(true);
                  }
                }}
                disabled={mfaLoading}
              />
              <span className={mfaEnabled ? 'text-green-600 font-medium' : 'text-gray-500'}>
                {mfaEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 2FA Setup Dialog */}
      <Dialog open={show2FASetup} onOpenChange={setShow2FASetup}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Enable Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              A verification code has been sent to your email address. Enter the code below to enable 2FA.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-blue-800">
                <Mail className="h-5 w-5" />
                <span className="font-medium">Check your email</span>
              </div>
              <p className="text-sm text-blue-700 mt-2">
                We've sent a 6-digit verification code to your email. The code expires in 10 minutes.
              </p>
            </div>
            <div>
              <Label htmlFor="verification_code">Enter 6-Digit Code</Label>
              <Input
                id="verification_code"
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                className="text-center text-2xl tracking-widest mt-2"
                autoFocus
              />
            </div>
            <Button
              onClick={handleVerify2FA}
              className="w-full"
              disabled={verificationCode.length !== 6 || mfaLoading}
            >
              {mfaLoading ? 'Verifying...' : 'Verify & Enable 2FA'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 2FA Disable Dialog */}
      <Dialog open={showDisable2FA} onOpenChange={setShowDisable2FA}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              Enter your password to disable 2FA. This will make your account less secure.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="disable_password">Password</Label>
              <Input
                id="disable_password"
                type="password"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                placeholder="Enter your password"
                className="mt-1"
              />
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowDisable2FA(false);
                  setDisablePassword('');
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={handleDisable2FA}
                disabled={!disablePassword || mfaLoading}
              >
                {mfaLoading ? 'Disabling...' : 'Disable 2FA'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatCard = ({ icon, value, label, color }) => {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    amber: 'bg-amber-100 text-amber-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    rose: 'bg-rose-100 text-rose-600',
  };

  return (
    <Card>
      <CardContent className="py-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
            {React.cloneElement(icon, { className: 'h-5 w-5' })}
          </div>
          <div>
            <p className="text-2xl font-bold">{value ?? '-'}</p>
            <p className="text-xs text-gray-500">{label}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SuperAdminOverview;
